import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

/** Store for managing scroll to new content state */
interface ScrollToNewContentState {
    /** ID of content currently being scrolled to */
    activeContentId: string | null
    /** Set of content IDs that are temporarily being added */
    temporaryContentIds: Set<string>
}

/** Actions for managing scroll to new content state */
interface ScrollToNewContentActions {
    /** Initiates scroll to newly created content */
    scrollToContent: (contentId: string) => void
    /** Marks content as temporarily being added */
    addTemporaryContent: (contentId: string) => void
    /** Removes content from temporary state */
    removeTemporaryContent: (contentId: string) => void
    /** Checks if content is currently being added */
    isAddingContent: (contentId: string) => boolean
}

export type ScrollToNewContentStore = ScrollToNewContentState &
    ScrollToNewContentActions

/**
 * Creates a Zustand store for managing scroll to new content state
 * @returns {ScrollToNewContentStore} A reactive store with scroll management methods
 */
export const useScrollToNewContentStore = create<ScrollToNewContentStore>(
    (set, get) => ({
        activeContentId: null,
        temporaryContentIds: new Set<string>(),

        scrollToContent: (contentId: string) =>
            set({ activeContentId: contentId }),

        addTemporaryContent: (contentId: string) =>
            set((state) => ({
                temporaryContentIds: new Set([
                    ...state.temporaryContentIds,
                    contentId,
                ]),
            })),

        removeTemporaryContent: (contentId: string) =>
            set((state) => {
                const newSet = new Set(state.temporaryContentIds)
                newSet.delete(contentId)
                return { temporaryContentIds: newSet }
            }),

        isAddingContent: (contentId: string) =>
            get().temporaryContentIds.has(contentId),
    })
)

/**
 * A custom hook that retrieves a shallowly selected value from the `useScrollToNewContentStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { activeContentId, isAddingContent } = useScrollToNewContentSelector((state) => ({
 *   activeContentId: state.activeContentId,
 *   isAddingContent: state.isAddingContent,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useScrollToNewContentSelector = <T>(
    selector: (state: ScrollToNewContentStore) => T
): T => useScrollToNewContentStore(useShallow(selector))
